import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Store } from '@ngrx/store';
import { OidcClientService } from '@upmc-healthplan-npm/oidc-client';
import { IPolCoreState } from '../../models/state.model';
import { POLAuthService } from '../../services/auth.service';
import * as polCoreSelectors from '../../state/core.selectors';
import * as polCoreActions from 'src/app/core/state/core.actions';
import { distinctUntilChanged, tap } from 'rxjs/operators';

@Component({
  selector: 'app-idle-timeout-wrapper',
  templateUrl: './idle-timeout-wrapper.component.html',
  styleUrls: ['./idle-timeout-wrapper.component.scss']
})
export class IdleTimeoutWrapperComponent implements OnInit {
  private readonly milliSecondsInOneSecond = 1000;
  private readonly secondsInOneMinute = 60;
  private inactivityTimeInSeconds = 17 * this.secondsInOneMinute; // 17 minutes
  private timeoutInSecondsAfterWarning = 3 * this.secondsInOneMinute; // 3 minutes
  showIdleWarning = false;
  warningIssuedAt = new Date();
  sessionWillEndAt = new Date();

  idleTimeoutReset$ = this.store.select(polCoreSelectors.idleTimeoutReset).pipe(
    distinctUntilChanged(),
    tap(() => this.reset())
  );

  // cache messages so times are duplicated when the modal is opened multiple times
  private readonly modalSubtitleTemplate = 'Session Timeout - Issued at ';
  private readonly modalBodyTemplate = '<p>If no action is taken by <b> replaceWithSessionEndTime, </b> your session will be ended and any unsaved data will be lost.</p> <br/>';

  constructor(
    private idle: Idle,
    private oidc: OidcClientService,
    private authService: POLAuthService,
    private store: Store<IPolCoreState>
  ) { }

  componentData = {
    class: 'popUpWrap',
    id: 'timeoutModal',
    role: 'alertdialog',
    ariaLabelledby: 'timeoutHeader',
    modal: {
      class: 'popUp'
    },
    header: {
      class: 'popUp-header',
      title: {
        text: 'Warning',
        id: 'timeoutHeader'
      },
      subTitle: {
        class: 'sub-header',
        text: this.modalSubtitleTemplate
      },
      closeIcon: true
    },
    body: {
      class: 'popUp-body',
      text: [
        '<p>Your UPMC Health Plan Provider Online session has been inactive for <b>17 minutes.</b> </p> <br/>',
        this.modalBodyTemplate,
        '<p> Select Continue Session to remain active and retain data that has been entered. </p>'
      ]
    },
    footer: {
      class: 'popUp-footer',
      buttons: [
        {
          class: 'ods-btn ods-btn-primary outline',
          text: 'Logout',
          id: 1
        },
        {
          class: 'ods-btn ods-btn-primary button2',
          text: 'Continue Session',
          id: 2
        }
      ]
    }
  };

  ngOnInit(): void {
    this.initIdleTimeInSeconds();
    this.reset();

    this.idleTimeoutReset$.subscribe();

    //This will reset the inactivityTime if session token refreshed
    this.store
      .select(polCoreSelectors.getRefreshTokenUrl)
      .subscribe((refreshToken: { isRefreshInProgress: boolean; url: string; resetInactivityTime: boolean }) => {
        if (refreshToken.resetInactivityTime) {
          this.continueSession();
          this.store.dispatch(polCoreActions.resetInactivityTime());
        }
      });
  }

  initIdleTimeInSeconds(): void {
    this.idle.setIdle(this.inactivityTimeInSeconds); // how long can they be inactive before considered idle, in seconds
    this.idle.setTimeout(this.timeoutInSecondsAfterWarning); // how long can they be idle before considered timed out, in seconds
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES); // provide sources that will "interrupt" aka provide events indicating the user is active
    this.idle.watch();

    // This will trigger after 17 min of inactivity
    this.idle.onIdleStart.subscribe(() => {
      // check if session timer is 59 minutes, if yes then refreh the token silently and show warning
      this.idle.setInterrupts([]);
      const user = this.oidc.getUser();
      this.warningIssuedAt = new Date(); // current time - 17 min (time when inactivity started)
      this.sessionWillEndAt = new Date(+new Date() + this.timeoutInSecondsAfterWarning * this.milliSecondsInOneSecond); // current time + 3 minutes (time when logout happens if no action)

      const exp = user.profile.exp * this.milliSecondsInOneSecond; // current token/session expiry time
      if (exp - +new Date() < this.timeoutInSecondsAfterWarning * this.milliSecondsInOneSecond) {
        // if the time left for session expiry is less than 3 minutes
        this.store.dispatch(
          polCoreActions.actionRefreshToken({ refreshToken: { isRefreshInProgress: true, url: this.authService.refreshTokenUrl() } })
        );
      }
      this.setTimingInfo();
      this.showIdleWarning = true;
    });

    this.idle.onTimeout.subscribe(() => {
      console.log(`Timeout Triggered at ${new Date(Date.now())}..........`);
      this.signOut();
    });
    this.idle.onTimeoutWarning.subscribe((seconds) => {
      console.log(`Timeout in ... ${seconds} seconds at ${new Date(Date.now())}.....`);
    });
  }

  reset(): void {
    // we'll call this method when we want to start/reset the idle process
    // reset any component state and be sure to call idle.watch()
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES); // provide sources that will "interrupt" aka provide events indicating the user is active
    this.idle.watch();
  }

  closeWarningModal(): void {
    this.showIdleWarning = false;
  }

  continueSession(): void {
    this.reset();
    this.closeWarningModal();
  }

  signOut(): void {
    console.log(`Timeout at ${new Date(Date.now())}.......... Signing out`);
    this.authService.signOutRedirect({ signOut: true });
  }

  setTimingInfo(): void {
    const warningIssuedTime = new DatePipe('en-US').transform(this.warningIssuedAt, 'h:mm a');
    const sessionEndTime = new DatePipe('en-US').transform(this.sessionWillEndAt, 'h:mm a');
    this.componentData.header.subTitle.text = `${this.modalSubtitleTemplate}${warningIssuedTime}`;
    this.componentData.body.text[1] = this.modalBodyTemplate.replace('replaceWithSessionEndTime', sessionEndTime);
  }
}
